import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { InviteClientHeader } from '@/components/invite-client/InviteClientHeader';
import { InviteClientProgressBar } from '@/components/invite-client/InviteClientProgressBar';

import { ChevronLeft, ChevronRight, Mail, Send } from 'lucide-react';

import { useToast } from '@/components/hooks/use-toast';
import { useInviteClientContext } from '@/components/invite-client/InviteClientContext';

import { cn } from '@client/lib';
import { getClientIntakeFormEmailTemplate } from '@shared';
import { selectSessionAdvisor, resetClientEmailState } from '@client/model';

type EmailViewMode = 'edit' | 'preview';

function EmailViewToggle({
  mode,
  onChange,
}: {
  mode: EmailViewMode;
  onChange: (mode: EmailViewMode) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-[3px]">
      <button
        type="button"
        onClick={() => onChange('edit')}
        className={cn(
          'rounded-[5px] px-4 py-1 text-sm font-medium text-gray-600 transition-all hover:text-gray-700',
          {
            'bg-gray-200/75 text-gray-950': mode === 'edit',
          },
        )}
      >
        Edit
      </button>
      <button
        type="button"
        onClick={() => onChange('preview')}
        className={cn(
          'rounded-[5px] px-4 py-1 text-sm font-medium text-gray-600 transition-all hover:text-gray-700',
          {
            'bg-gray-200/75 text-gray-950': mode === 'preview',
          },
        )}
      >
        Preview
      </button>
    </div>
  );
}

export function InviteClientCustomizeEmail() {
  const { toast } = useToast();

  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  const [emailViewMode, setEmailViewMode] = useState<EmailViewMode>('edit');

  const {
    inviteClientConfig,
    isLoading,
    isTestEmailLoading,
    isTestEmailComplete,
    inviteCount,
    isBulkInvite,
    handleStepChange,
    handleSendEmail,
    handleSendTestEmail,
    handleConfigChange,
  } = useInviteClientContext();

  const { emailBody, firstName, isComplianceConfirmed } = inviteClientConfig;

  useEffect(() => {
    if (isTestEmailComplete) {
      toast({
        title: 'Test email sent',
        description: 'Test email sent to ' + advisor.advisorEmail,
        variant: 'default',
        className: '-translate-y-5 border-green-500 bg-green-50 text-green-800',
      });
      dispatch(resetClientEmailState());
    }
  }, [isTestEmailComplete, advisor.advisorEmail, toast, dispatch]);

  return (
    <>
      <InviteClientProgressBar
        currentStep={2}
        stepLabel="Customize"
      />

      <InviteClientHeader
        title="Customize email"
        description="Personalize the invitation email or use our default template"
        isOptional
      />

      <div className="mt-6">
        <div className="mb-4 flex items-center justify-between">
          <EmailViewToggle
            mode={emailViewMode}
            onChange={setEmailViewMode}
          />
          <Button
            size="sm"
            type="button"
            variant="outline"
            onClick={handleSendTestEmail}
            disabled={isLoading || isTestEmailLoading}
            className="h-9"
          >
            <Mail className="size-4" />
            {isTestEmailLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border">
          {emailViewMode === 'edit' ? (
            <Textarea
              value={emailBody}
              onChange={({ target }) => handleConfigChange('emailBody', target.value)}
              className="min-h-[280px] resize-none border-none p-4 text-gray-700 focus-visible:ring-0"
              placeholder="Write your email message..."
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: getClientIntakeFormEmailTemplate({
                  advisorFirstName: advisor.advisorFirstName,
                  intakeFormLink: '',
                  clientFirstName: firstName,
                  advisorProvidedBody: emailBody ?? '',
                  isPreview: true,
                }),
              }}
              className="max-h-[280px] min-h-[280px] overflow-auto  text-gray-900"
            />
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          className="mt-4 w-full"
          onClick={() => handleStepChange('email-additional-settings')}
        >
          Additional settings
          <ChevronRight className="size-4" />
        </Button>

        {isBulkInvite && (
          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              id="compliance"
              checked={isComplianceConfirmed}
              onCheckedChange={checked => handleConfigChange('isComplianceConfirmed', !!checked)}
              className="translate-y-0.5"
            />
            <Label
              htmlFor="compliance"
              className="text-sm italic text-gray-700"
            >
              I confirm that I have either obtained compliance approval or understand this message
              may require compliance review. I acknowledge that sending this email is my
              responsibility.
            </Label>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => handleStepChange('email')}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>

        <Button
          onClick={handleSendEmail}
          disabled={isLoading || (isBulkInvite && !isComplianceConfirmed)}
        >
          <Send className="size-4" />
          {isLoading ? 'Sending...' : `Send ${inviteCount} invite${inviteCount > 1 ? 's' : ''}`}
        </Button>
      </div>
    </>
  );
}
